/*
 * Who a laboratory order can be placed under (KIN-2100 / KIN-2112).
 *
 * A lab order goes out under a GP's Medical Council Number — it is in MSH.4 and in the
 * message control id. The backend used to take it from the signed-in user, so a nurse
 * placing an order for a GP either failed with a misleading error or sent her own
 * number as if it were a GP's. The template request now names the ordering GP.
 *
 * Eligibility is a 6-digit Medical Council Number, which is what the message needs and
 * what the backend checks — not a role name. "Nurse" and "GP" are labels a practice
 * sets for itself and spells as it likes; a number either goes on an order or it
 * doesn't.
 */

const MEDICAL_COUNCIL_NUMBER = /^\d{6}$/;

export const isEligibleOrderingGp = (staffMember) =>
  !!staffMember &&
  staffMember.active !== false &&
  staffMember.disabled !== true &&
  staffMember.deleted !== true &&
  MEDICAL_COUNCIL_NUMBER.test(String(staffMember.professionalMedicalNumber ?? "").trim());

const toStaffName = (staffMember) =>
  [staffMember?.title, staffMember?.firstName, staffMember?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

/** The practice's GPs, as select options, by name. */
export const buildOrderingGpOptions = (staffMembers) =>
  (staffMembers ?? [])
    .filter(isEligibleOrderingGp)
    .map((staffMember) => ({
      value: String(staffMember.id),
      label: toStaffName(staffMember) || `Staff member ${staffMember.id}`,
    }))
    .sort((one, other) => one.label.localeCompare(other.label));

/**
 * What the picker starts on.
 *
 * The signed-in user when they are a GP themselves, so a GP ordering for their own
 * patient sees no change. Nothing otherwise: a nurse has to say whose number goes on
 * the order, and a default would be a guess at that.
 */
export const resolveDefaultOrderingGp = ({ staffMembers, currentStaffId }) => {
  if (!currentStaffId) return "";

  const currentStaffMember = (staffMembers ?? []).find(
    (staffMember) => String(staffMember.id) === String(currentStaffId)
  );

  return isEligibleOrderingGp(currentStaffMember) ? String(currentStaffId) : "";
};

/**
 * What to send as `orderingStaffId`.
 *
 * Nothing when it is the signed-in user: the backend reads the caller's own id then,
 * which is the behaviour from before the field existed, and is the one case Healthlink
 * has confirmed. Placing an order under anybody else is answered 409 for now.
 */
export const toOrderingStaffId = ({ orderingGpId, currentStaffId }) => {
  if (!orderingGpId) return null;
  if (String(orderingGpId) === String(currentStaffId)) return null;

  return Number(orderingGpId);
};
